import React, { useState, useEffect } from 'react';
import { Plus, MoreVertical } from 'lucide-react';
import './styles/ManageAddresses.css';
import NewAddressModal from './NewAddressModal/NewAddressModal';
import { Address } from '../dto/Address.dto';
import { AddressService } from '../service/Address.service';
import { ApiService } from '../../../shared/service/Apiservice';
import axios from 'axios';

const ManageAddresses = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [optionsMenuOpen, setOptionsMenuOpen] = useState<string | null>(null);
  const [addressToEdit, setAddressToEdit] = useState<Address | null>(null);

  useEffect(() => {
    if (!optionsMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const menuElements = document.querySelectorAll('.address-options-menu');
      let clickedInside = false;
      menuElements.forEach(menu => {
        if (menu.contains(event.target as Node)) {
          clickedInside = true;
        }
      });
      if (!clickedInside) setOptionsMenuOpen(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [optionsMenuOpen]);

  // Create API service instances
  const apiService = React.useMemo(() => new ApiService(axios.create()), []);
  const addressService = React.useMemo(() => new AddressService(apiService), [apiService]);
  // Fetch addresses from API
  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await addressService.getUserAddresses();
      if (response.success && response.data) {
        setAddresses(response.data);
      }
    } catch (error) {
      // Optionally show error toast
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  // Add address logic (to be passed to modal)
  const handleAddAddress = async (newAddress: Address, addressId?: string) => {
    try {
      await addressService.addOrEditAddress({
        ...newAddress,
        is_add_usecase: !addressId,
        address_id: addressId,
      });
      fetchAddresses();
      setIsModalOpen(false);
      setAddressToEdit(null);
    } catch (error) {
    }
  };

  const handleEditAddress = (address: Address) => {
    setAddressToEdit(address);
    setIsModalOpen(true);
    setOptionsMenuOpen(null);
  };

  const handleDeleteAddress = async (addressId: string) => {
    try {
      await addressService.deleteAddress(addressId);
      fetchAddresses();
    } catch (error) {
      // Optionally show error toast
    }
  };
    // Make address default logic
    const handleMakeDefault = async (addressId: string) => {
      try {
        await addressService.setDefaultAddress(addressId);
        fetchAddresses();
        setOptionsMenuOpen(null);
      } catch (error) {
        // Optionally show error toast
      }
    };

  return (
    <div className="manage-addresses-container">
      <div className="main-card">
        <div className="manage-addresses-header-row">
          <h2 className="heading">Manage Addresses</h2>
          <div className="new-address-btn" onClick={() => setIsModalOpen(true)}>
            <Plus className="plus-icon" size={30} style={{ color: '#FFC621' }} />
          </div>
        </div>
        <div className="addresses-list">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
          ) : addresses.length > 0 ? addresses.map((addr) => (
            <div key={addr._id} className="address-card">
              <div className="address-content">
                <div className="address-type">{addr.label}</div>
                <div className="address-name">{addr.name}</div>
                <div className="address-phone">{addr.phone_number}</div>
                <div className="address-text">
                  {addr.primary_address_line}
                  {addr.secondary_address_line && `, ${addr.secondary_address_line}`}
                  {addr.locality && `, ${addr.locality}`}
                  {addr.landmark && `, ${addr.landmark}`}
                </div>
              </div>
              <div className="more-options" style={{ position: 'relative' }} onClick={() => setOptionsMenuOpen(addr._id!)}>
                <MoreVertical size={20} color="#999" />
                {optionsMenuOpen === addr._id && (
                  <div className="address-options-menu">
                    <button className="address-option-btn edit" onClick={(e) => { e.stopPropagation(); handleEditAddress(addr); }}>Edit</button>
                    <button className="address-option-btn default" onClick={(e) => { e.stopPropagation(); handleMakeDefault(addr._id!); }}>Make Default</button>
                    <button className="address-option-btn delete" onClick={(e) => { e.stopPropagation(); handleDeleteAddress(addr._id!); }}>Delete</button>
                  </div>
                )}
              </div>
            </div>
          )) : (
            <div style={{ textAlign: 'center', padding: '2rem' }}>No addresses found.</div>
          )}
        </div>
      </div>
      <NewAddressModal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setAddressToEdit(null); }} 
        onAddAddress={handleAddAddress}
        addressToEdit={addressToEdit}
      />
    </div>
  );
};

export default ManageAddresses;